import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Button,
  Collapse,
  Switch,
  TextField,
} from "@mui/material";
import { Box } from "react-feather";
import { useHistory } from "react-router-dom";
import { useSWRConfig } from "swr";
import { useDebounce } from "use-debounce";
import API from "../../../axios/instances/API";
import ExternalLinkDjango from "./ExternalLinkDjango";
import useGlobalStore from "src/store/useGlobalStore";
import { PATHS } from "src/routes";

const CustomRenderOption = ({ option, ...props }) => (
  <div
    {...props}
    style={{
      height: 40,
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    }}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
      }}
    >
      {option?.organization_name && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 4,
          }}
        >
          <Box height={10} width={10} color="slategrey" />
          <span
            style={{
              fontSize: 11,
              color: "slategrey",
              fontWeight: 500,
              lineHeight: 1.3,
              textTransform: "uppercase",
            }}
          >
            {option?.organization_name} Organization
          </span>
        </div>
      )}
      <span
        style={{
          fontSize: 12,
          color: "black",
          fontWeight: 500,
          lineHeight: 1.3,
        }}
      >
        {option?.name}
      </span>
    </div>
    <div
      style={{
        padding: "2px 4px",
        background: "#eeeeee",
        borderRadius: 4,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <span
        style={{
          fontSize: 10,
          color: "slategrey",
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {option?.permissions_template_name}
      </span>
    </div>
  </div>
);

const SuperDisguise = ({ setError, handleClose }) => {
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [clearCompanies, setClearCompanies] = useState(true);
  const [readOnly, setReadOnly] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [searchText] = useDebounce(search, 300);

  const refetchConfigBackground = useGlobalStore(
    (state) => state.refetchConfigBackground,
  );
  const history = useHistory();

  const { mutate } = useSWRConfig();

  const clearCache = () => mutate(() => true, undefined, { revalidate: true });

  const getSearchResults = async () => {
    try {
      const { data } = await API.get(
        `/dashboard/account/superuser-company-switch/?search=${searchText}`,
      );
      setSearchResults(data?.companies);
    } catch (error) {
      setError("Error getting search results");
    }
  };

  useEffect(() => {
    if (searchText.length < 3) return undefined;

    getSearchResults();
  }, [searchText]);

  const handleDisguise = async () => {
    try {
      setSwitching(true);

      await API.post("/dashboard/account/superuser-company-switch/", {
        company_id: selected,
        read_only: readOnly,
        clear_current_companies: clearCompanies,
      });
      await refetchConfigBackground();
      clearCache();
      history.push(PATHS.HOME);
      handleClose();
    } catch (error) {
      setSwitching(false);
      setError("Error disguising you.");
    }
  };

  return (
    <>
      <Autocomplete
        autoFocus={true}
        size="small"
        filterOptions={(x) => x}
        fullWidth
        onClose={() => {
          setSearchResults([]);
        }}
        onInputChange={(e) => {
          return e.target.innerText
            ? setSearch(e.target.innerText)
            : setSearch("");
        }}
        renderOption={(props, option) => (
          <CustomRenderOption {...props} option={option} />
        )}
        onChange={(_, option) => setSelected(option?.id)}
        getOptionLabel={(option) => option.name}
        noOptionsText={
          search.length > 2
            ? "No companies found for search..."
            : "Type to search..."
        }
        options={searchResults}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            label="Search For Company"
            color="primary"
            fullWidth
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
      />
      <div style={{ height: 10 }} />
      <Collapse in={selected} style={{ width: "100%" }}>
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",

              padding: "4px 12px",
              borderRadius: 4,
              background: "#eeeeee",
            }}
          >
            <span
              style={{
                fontWeight: 500,
                color: readOnly ? "#337ab7" : "slategrey",
                fontSize: 12,
              }}
            >
              Read Only in this company?
            </span>
            <Switch
              checked={readOnly}
              size="small"
              onChange={() => setReadOnly((p) => !p)}
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "4px 12px",
              width: "100%",
              borderRadius: 4,
              background: "#eeeeee",
            }}
          >
            <span
              style={{
                fontWeight: 500,
                color: clearCompanies ? "#337ab7" : "slategrey",
                fontSize: 12,
              }}
            >
              Clear currently managed companies after the switch?
            </span>
            <Switch
              checked={clearCompanies}
              onChange={() => setClearCompanies((p) => !p)}
              size="small"
            />
          </div>
          <ExternalLinkDjango companyId={selected} />
        </div>
      </Collapse>
      <div style={{ height: 10 }} />
      <Button
        size="small"
        variant="contained"
        color="primary"
        style={{ marginLeft: "auto" }}
        disabled={!selected || switching}
        onClick={handleDisguise}
      >
        Disguise
      </Button>
    </>
  );
};

export default SuperDisguise;
